import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Pause, Play, RotateCcw, RotateCw } from 'lucide-react';
import './call-recording-player.css';

const validDuration = (value: number | null | undefined) => typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
const clock = (seconds: number) => {
 const value = Math.max(0, Math.floor(seconds));
 const hours = Math.floor(value / 3600);
 const minutes = Math.floor(value / 60);
 return `${hours ? `${hours}:${String(minutes % 60).padStart(2, '0')}` : minutes}:${String(value % 60).padStart(2, '0')}`;
};

export function CallRecordingPlayer({ src, label = 'Call recording', durationSeconds, compact = false }: { src: string; label?: string; durationSeconds?: number | null; compact?: boolean }) {
 const audio = useRef<HTMLAudioElement>(null);
 const [playing, setPlaying] = useState(false);
 const [position, setPosition] = useState(0);
 const [mediaDuration, setMediaDuration] = useState(0);
 const [speed, setSpeed] = useState(1);
 const [error, setError] = useState('');
 const duration = mediaDuration || validDuration(durationSeconds);
 useEffect(() => { setPlaying(false); setPosition(0); setMediaDuration(0); setSpeed(1); setError(''); }, [src]);
 const seek = (value: number) => {
  if (!audio.current || !duration) return;
  const target = Math.max(0, Math.min(duration, value));
  audio.current.currentTime = target;
  setPosition(target);
 };
 const toggle = async () => {
  const media = audio.current;
  if (!media) return;
  if (playing) { media.pause(); return; }
  setError('');
  try { await media.play(); }
  catch { setPlaying(false); setError('The recording could not be played. Please try again.'); }
 };
 return <div className={`call-recording-player${compact ? ' call-recording-player--compact' : ''}`} style={{'--recording-progress': `${duration ? Math.min(100, position / duration * 100) : 0}%`} as CSSProperties} role="group" aria-label={`${label} player`}>
  <audio key={src} ref={audio} aria-label={label} src={src} preload="metadata" crossOrigin="use-credentials"
   onLoadedMetadata={event => { const total = validDuration(event.currentTarget.duration); if (total) setMediaDuration(total); }}
   onDurationChange={event => { const total = validDuration(event.currentTarget.duration); if (total) setMediaDuration(total); }}
   onTimeUpdate={event => setPosition(event.currentTarget.currentTime)}
   onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
   onError={() => { setPlaying(false); setError('The recording is unavailable or could not be loaded.'); }} />
  <div className="call-recording-timeline">
   <input type="range" min="0" max={duration || 1} step="0.1" value={Math.min(position, duration || 0)} disabled={!duration} aria-label="Recording position" aria-valuetext={`${clock(position)} of ${duration ? clock(duration) : 'unknown duration'}`} onChange={event => seek(Number(event.target.value))} />
   <span className="call-recording-time" aria-label="Recording time">{clock(position)} / {duration ? clock(duration) : '--:--'}</span>
  </div>
  <div className="call-recording-controls">
   <button type="button" className="call-recording-play" aria-label={playing ? 'Pause recording' : 'Play recording'} onClick={() => void toggle()}>{playing ? <Pause size={16} aria-hidden /> : <Play size={16} aria-hidden />}</button>
   <button type="button" aria-label="Back 15 seconds" disabled={!duration} onClick={() => seek((audio.current?.currentTime ?? position) - 15)}><RotateCcw size={15} aria-hidden /><span>15s</span></button>
   <button type="button" aria-label="Forward 15 seconds" disabled={!duration} onClick={() => seek((audio.current?.currentTime ?? position) + 15)}><RotateCw size={15} aria-hidden /><span>15s</span></button>
   <label className="call-recording-speed"><span>Speed</span><select aria-label="Playback speed" value={speed} onChange={event => {
    const value = Number(event.target.value); setSpeed(value); if (audio.current) audio.current.playbackRate = value;
   }}><option value={1}>1×</option><option value={2}>2×</option></select></label>
  </div>
  {error && <p className="call-recording-error" role="alert">{error}</p>}
 </div>;
}
